import moment from 'moment-timezone'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { SET_DISPLAY_TIMEZONE } from 'Constants'
import { store } from '../store.index'
import {
  STORAGE_KEY,
  resolveDisplayMode,
  toDisplayMoment,
  offsetsMatch,
  zoneAbbr,
  displayCalendarToday as calendarToday,
  expandDatesToDisplayDays as expandDays,
  displayWeekStartKey as weekStartKey,
  calendarWeekCount
} from './displayTimezone.cjs'

const listeners = new Set()
let knownRegionTz = null

export function rememberRegionTimezone(regionTz) {
  if (regionTz) knownRegionTz = regionTz
}

export function getDisplayMode() {
  let storageValue = null
  try {
    storageValue = localStorage.getItem(STORAGE_KEY)
  } catch {
    storageValue = null
  }
  let reduxValue = null
  try {
    reduxValue = store.getState()?.centerreducer?.displayTimezone
  } catch {
    reduxValue = null
  }
  return resolveDisplayMode(storageValue, reduxValue)
}

export function setDisplayMode(mode) {
  const next = mode === 'local' ? 'local' : 'studio'
  try {
    localStorage.setItem(STORAGE_KEY, next)
  } catch {
    /* private mode */
  }
  try {
    store.dispatch({ type: SET_DISPLAY_TIMEZONE, value: next })
  } catch {
    /* store not ready */
  }
  listeners.forEach(listener => listener())
}

export function subscribeDisplayMode(listener) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function regionTimezone() {
  return knownRegionTz || moment.defaultZone?.name || null
}

function allowFlag(center) {
  const flag = center?.settings?.calendar?.allow_local_timezone
  return flag === true || flag === 1 || flag === '1'
}

export function allowLocalTimezone() {
  try {
    const state = store.getState().centerreducer || {}
    const centersById = state.centersById || {}
    const centersByRegion = state.centersByRegion || {}
    let regionId = null
    try {
      regionId = localStorage.getItem('region_id')
    } catch {
      regionId = null
    }
    const regionCenterIds = regionId ? centersByRegion[regionId] : null
    if (!regionCenterIds?.length) return false
    return regionCenterIds.some(id => allowFlag(centersById[id]))
  } catch {
    return false
  }
}

export function displayMoment(input) {
  return toDisplayMoment(input, {
    mode: getDisplayMode(),
    allow: allowLocalTimezone(),
    regionTz: regionTimezone(),
    localTz: moment.tz.guess(),
    moment
  })
}

export function displayDayKey(input) {
  if (input == null || input === '') return ''
  const shown = displayMoment(input)
  return shown?.isValid?.() ? shown.format('YYYY-MM-DD') : ''
}

function displayOptions() {
  return {
    mode: getDisplayMode(),
    allow: allowLocalTimezone(),
    regionTz: regionTimezone(),
    localTz: moment.tz.guess(),
    moment
  }
}

export function displayCalendarToday() {
  return calendarToday(displayOptions())
}

export function expandDatesToDisplayDays(dates) {
  return expandDays(dates, displayOptions())
}

export function displayWeekStartKey(weekStartsOnSunday) {
  return weekStartKey(weekStartsOnSunday, displayOptions())
}

export function displayCalendarWeeks(totalWeeks, weekStartsOnSunday) {
  return calendarWeekCount(totalWeeks, weekStartsOnSunday, { moment })
}

export function displayZoneLabel() {
  const showingLocal = getDisplayMode() === 'local' && allowLocalTimezone()
  const tz = showingLocal ? moment.tz.guess() : regionTimezone()
  return zoneAbbr(tz, moment)
}

export function shouldShowTimezoneToggle(regionTz) {
  if (!regionTz || !allowLocalTimezone()) return false
  return !offsetsMatch(regionTz, moment.tz.guess(), moment)
}

export function useDisplayMode() {
  const reduxMode = useSelector(state => state.centerreducer.displayTimezone)
  const [storageMode, setStorageMode] = useState(getDisplayMode)
  useEffect(
    () => subscribeDisplayMode(() => setStorageMode(getDisplayMode())),
    []
  )
  return resolveDisplayMode(storageMode, reduxMode)
}

export function timezoneToggleLabels(regionTz) {
  return {
    studio: `Studio time (${zoneAbbr(regionTz, moment)})`,
    local: `My time (${zoneAbbr(moment.tz.guess(), moment)})`
  }
}
